import { getLocale, type Locale } from '@/lib/locale';

export const GROK_VIDEO_GENERATOR_PATH = '/text-to-video';

export const GROK_VIDEO_GENERATOR_SEO_TITLE =
  'Grok Video Generator: Create AI Videos from Text';

export const GROK_VIDEO_GENERATOR_SEO_DESCRIPTION =
  'Use this Grok video generator to turn a written scene into a short AI video. Choose the model, ratio, duration, resolution, and mode, then create a draft.';

export const GROK_VIDEO_GENERATOR_DATE_PUBLISHED = '2026-06-30';

export const GROK_VIDEO_GENERATOR_LAST_UPDATED = '2026-07-28';

export const GROK_VIDEO_GENERATOR_LAST_UPDATED_LABEL = 'July 28, 2026';

export const GROK_VIDEO_GENERATOR_KEYWORDS = [
  'grok video generator',
  'grok ai video generator',
  'grok text to video',
  'grok ai text to video',
  'grok imagine video generator',
  'grok imagine video length limit 2026',
  'grok imagine video free limit',
  'grok imagine rate limit reached',
].join(', ');

export const GROK_VIDEO_GENERATOR_QUICK_ANSWER =
  'A Grok video generator turns a written scene into a short AI video draft without requiring a source image. It is designed for creators, marketers, founders, and design teams that need control over the model, aspect ratio, duration, resolution, and Grok mode. Describe one scene, choose the output settings, generate the clip, then review or download the result.';

export const GROK_VIDEO_GENERATOR_TOOL_DETAILS = [
  {
    label: 'Primary workflow',
    value: 'Grok video generator from a written scene',
  },
  {
    label: 'Best input',
    value: 'A clear scene prompt with subject, action, camera, and mood',
  },
  {
    label: 'Last updated',
    value: GROK_VIDEO_GENERATOR_LAST_UPDATED_LABEL,
  },
];

export const GROK_VIDEO_GENERATOR_FEATURES = [
  {
    title: 'Turn Text Prompts Into Short AI Videos',
    body: 'The Grok video generator starts with a written scene rather than a source image. A useful prompt explains the subject, action, camera movement, setting, mood, and publishing format.',
  },
  {
    title: 'Control Camera, Motion, and Scene Direction',
    body: 'Guide the Grok video generator with motion language such as slow push-in, handheld pan, orbit shot, product reveal, light sweep, wind movement, or cinematic close-up.',
  },
  {
    title: 'Choose Format Settings Before You Spend Credits',
    body: 'Select aspect ratio, duration, resolution, model, and Grok mode before generation. This helps the Grok AI video generator match social, ad, storyboard, or product-preview goals before you spend credits.',
  },
  {
    title: 'Review, Save, and Reuse Video Drafts',
    body: 'Preview generated clips, compare recent attempts, open your video history, and download the best draft when the creative direction is ready. The Grok video generator keeps review and iteration in the same workflow.',
  },
];

export const GROK_VIDEO_GENERATOR_STEPS = [
  {
    name: 'Write the scene',
    text: 'In the Grok video generator, describe one clear scene: who or what appears, what moves, how the camera behaves, what the light feels like, and where the clip will be used.',
  },
  {
    name: 'Choose video settings',
    text: 'Pick the model, aspect ratio, duration, resolution, and Grok mode that fit the output goal. This keeps the video draft aligned with Reels, Shorts, TikTok, X posts, ads, or storyboards.',
  },
  {
    name: 'Review and refine',
    text: 'Check subject consistency, motion quality, framing, and publishing fit. Download the result or adjust the Grok video generator prompt for another version.',
  },
];

export const GROK_VIDEO_GENERATOR_SUPPORTED_SETTINGS = [
  {
    setting: 'Prompt input',
    options: 'Text scene prompt',
    bestFor:
      'Short video ideas with a clear subject, action, camera move, mood, and output goal.',
  },
  {
    setting: 'Aspect ratio',
    options: '2:3, 3:2, 1:1, 9:16, or 16:9',
    bestFor:
      'Vertical social clips, square feed posts, wide ads, product previews, and storyboard frames.',
  },
  {
    setting: 'Duration',
    options: '6-30 seconds',
    bestFor:
      'Fast creative tests, short hooks, social drafts, campaign previews, and longer scene concepts.',
  },
  {
    setting: 'Resolution',
    options: '480p or 720p in the Grok video generator',
    bestFor: 'Quick low-cost drafts at 480p or sharper review clips at 720p.',
  },
  {
    setting: 'Grok mode',
    options: 'Normal, Fun, or Spicy where supported',
    bestFor:
      'Matching the creative tone while keeping public or brand work under human review.',
  },
];

export const GROK_VIDEO_GENERATOR_EXAMPLES = [
  {
    title: 'Product launch teaser',
    prompt:
      'A matte black wireless speaker on a stone table, slow camera push-in, soft gold rim light, subtle dust particles, premium product ad mood.',
    setting: '9:16, 6 seconds, 720p',
    result:
      'A vertical Grok video generator draft for ads, product pages, or social launch posts.',
  },
  {
    title: 'Cinematic character scene',
    prompt:
      'A cyberpunk courier standing in neon rain, jacket moving in the wind, slow orbit camera, reflective street, dramatic blue and pink lighting.',
    setting: '16:9, 10 seconds, 720p',
    result:
      'A short character-motion concept for storyboards, game pitches, or mood tests.',
  },
  {
    title: 'Founder announcement clip',
    prompt:
      'A clean startup office with a laptop opening to reveal a new app, gentle handheld movement, morning light, confident launch announcement mood.',
    setting: '1:1, 6 seconds, 720p',
    result:
      'A square announcement draft for LinkedIn, X, product updates, or pitch visuals.',
  },
];

export const GROK_VIDEO_GENERATOR_MEDIA_PROOF = [
  {
    title: 'Creator motion scene from a written prompt',
    label: 'Video generator demo',
    prompt:
      'A cinematic creator scene with clear subject motion, smooth camera movement, dramatic lighting, and social-ready pacing.',
    setting: 'Text prompt, 16:9, 15.047 seconds, 852 x 480 review draft',
    caption:
      'This text-to-video example shows why a Grok video generator needs a complete scene prompt: subject, motion, camera, lighting, mood, and format all shape the final draft.',
    videoUrl: '/text-to-video/text-to-video-creator-motion-scene.mp4',
    poster: '/text-to-video/text-to-video-creator-motion-scene-poster.jpg',
    alt: 'Grok video generator text-to-video demo showing a cinematic creator scene from a written prompt',
    uploadDate: '2026-05-19',
  },
  {
    title: 'Portrait performance scene from a written prompt',
    label: 'Video generator demo',
    prompt:
      'Create a portrait performance scene with controlled lighting, expressive motion, a slow camera move, and a polished social video finish.',
    setting: 'Text prompt, 16:9, 8 seconds, 1280 x 720 review draft',
    caption:
      'This Grok video generator example shows how a written prompt can define subject framing, lighting, motion, and pacing before creating a reviewable draft.',
    videoUrl: '/text-to-video/text-to-video-portrait-performance-scene.mp4',
    poster:
      '/text-to-video/text-to-video-portrait-performance-scene-poster.jpg',
    alt: 'Grok video generator demo showing a portrait performance scene created from a written prompt',
    uploadDate: '2026-05-19',
  },
  {
    title: 'Perfume product scene from a written prompt',
    label: 'Video generator demo',
    prompt:
      'Create a polished perfume product scene with readable motion, controlled camera timing, premium lighting, and a review-ready output.',
    setting:
      'Text prompt, product scene, 10.04 seconds, 858 x 480 review draft',
    caption:
      'This Grok video generator example shows the review standard to apply after generation: check motion, framing, product clarity, and publishing fit.',
    videoUrl: '/text-to-video/text-to-video-perfume-product-scene.mp4',
    poster: '/text-to-video/text-to-video-perfume-product-scene-poster.jpg',
    alt: 'Grok AI text to video demo showing a perfume product scene generated from a written prompt',
    uploadDate: '2026-06-05',
  },
];

export const GROK_VIDEO_GENERATOR_COMPARISON_ROWS = [
  {
    factor: 'Starting point',
    thisTool:
      'Start the Grok video generator with a written scene when you do not have a source image yet.',
    genericTool:
      'Start Grok image to video with an existing photo, illustration, product frame, or generated image.',
  },
  {
    factor: 'Best for',
    thisTool:
      'Concept drafts, social hooks, product ideas, storyboards, and scenes that begin as text.',
    genericTool:
      'Animating a composition, character, product, or visual style that already exists in one frame.',
  },
  {
    factor: 'Direction method',
    thisTool:
      'Define the subject, action, camera, lighting, mood, and output format in the prompt.',
    genericTool:
      'Preserve the source frame, then describe the motion, camera behavior, and scene change.',
  },
  {
    factor: 'Choose this when',
    thisTool:
      'The idea is clear in words and you want the model to establish the first visual composition.',
    genericTool:
      'The first frame is already strong and you want more control over the subject or product identity.',
  },
];

export const GROK_VIDEO_GENERATOR_USE_CASES = [
  {
    title: 'Social Creators Testing Short Hooks',
    body: 'Write a hook-style scene, choose 9:16, and use the Grok video generator to test motion before editing a final post for TikTok, Reels, Shorts, or X.',
  },
  {
    title: 'Marketing Teams Exploring Product Video Ideas',
    body: 'The Grok video generator helps marketing teams turn a campaign line into a product reveal, lifestyle scene, or ad concept before booking production or polishing a final asset.',
  },
  {
    title: 'Designers and Founders Building Storyboards',
    body: 'Use Grok video generator prompts to visualize app launches, pitch moments, game scenes, cinematic moods, or brand concepts as short reviewable clips.',
  },
];

export const GROK_VIDEO_GENERATOR_VALUE_ROWS = [
  {
    need: 'Start without a source image',
    value:
      'Use the Grok text to video workflow when the idea is clear but no visual asset exists yet.',
  },
  {
    need: 'Reduce vague video prompts',
    value:
      'Structure the idea around subject, action, camera, lighting, mood, and format.',
  },
  {
    need: 'Match publishing channels',
    value:
      'Choose vertical, square, tall, or wide aspect ratios before generation.',
  },
  {
    need: 'Iterate with less friction',
    value:
      'Review the generated clip, adjust the prompt, and keep recent Grok AI video generator results in history.',
  },
];

export const GROK_VIDEO_GENERATOR_LIMITS = [
  'The Grok video generator works best with one primary subject and one main motion direction.',
  'Complex crowds, tiny text, detailed hands, and overloaded action lists can reduce consistency.',
  'Generated videos should be reviewed before ads, client work, product pages, or public social posts.',
  'Avoid protected likenesses, trademarks, copyrighted characters, and private personal data unless you have the right to use them.',
  'This tool is an independent Grok Imagine workflow and is not affiliated with xAI, Grok, X, or the official xAI team.',
];

export const GROK_VIDEO_GENERATOR_FAQS = [
  {
    question: 'What is a Grok video generator?',
    answer:
      'A Grok video generator turns a written prompt into a short AI video draft. In this tool, you describe the scene, choose video settings, and create a clip from text.',
  },
  {
    question: 'Does Grok AI generate videos in 2026?',
    answer:
      'Yes. Grok Imagine supports video generation, while this independent tool provides a video creation workflow through the available models. Access, output settings, and limits can change by provider and plan.',
  },
  {
    question: 'Can Grok generate videos from text?',
    answer:
      'Yes. Use the Grok video generator when your idea starts as text and you do not have a source image yet. If you already have a strong first frame, the Grok image to video workflow is usually a better fit.',
  },
  {
    question: 'Can I use this as a Grok AI text to video tool?',
    answer:
      'Yes. The Grok video generator supports written scene prompts for users who want to start from an idea instead of an uploaded image.',
  },
  {
    question: 'Is Grok video generation free or paid?',
    answer:
      'Availability and cost depend on the selected model, video settings, account credits, and current product limits. Check the credit cost before generation, then use lower-duration drafts for early tests.',
  },
  {
    question: 'Is there a Grok Imagine video free limit?',
    answer:
      'Free video availability depends on the current credit offer, selected model, duration, resolution, and account status. Check the displayed generation cost before starting, and use a shorter draft while testing the prompt.',
  },
  {
    question: 'What is the Grok AI video generation length limit?',
    answer:
      'The Grok Imagine video model on this page supports durations from 6 to 30 seconds. Use shorter clips for prompt testing, then increase the duration after the scene, camera, and motion direction are clear.',
  },
  {
    question: 'Why did I hit a Grok video rate limit?',
    answer:
      'A Grok video rate limit usually means too many generation requests were sent in a short window, or the selected model is under account, queue, or credit pressure. Stop repeated retries, wait or slow the queue, then continue from the best saved prompt or image.',
  },
  {
    question: 'What settings should I choose in this video generator?',
    answer:
      'The Grok video generator lets you choose the model, aspect ratio, duration, resolution, and Grok mode for your publishing channel. Use 9:16 for vertical social clips, 16:9 for wide storyboards, and 720p when you want a sharper review draft.',
  },
  {
    question: 'Is this Grok video generator good for text to video?',
    answer:
      'Yes. This tool is designed for text to video tasks where the idea starts as a written scene. If you already have a source image, use the image to video workflow instead.',
  },
  {
    question: 'What should I write in a video generator prompt?',
    answer:
      'A Grok video generator prompt should include the subject, action, camera movement, setting, lighting, mood, duration, aspect ratio, and target platform. Clear motion instructions usually work better than short generic prompts.',
  },
  {
    question: 'Is this the official Grok or xAI website?',
    answer:
      'No. This is an independent Grok Imagine workflow tool and is not affiliated with xAI, Grok, X, or the official xAI team.',
  },
  {
    question: 'Can I use generated videos commercially?',
    answer:
      'Commercial use depends on the selected model, platform terms, input rights, and final output review. Avoid protected characters, trademarks, private likenesses, and copyrighted material unless you have permission.',
  },
];

export const GROK_VIDEO_GENERATOR_SCHEMA_FEATURES = [
  'Video generation from written scene prompts',
  'Prompt controls for subject, action, camera movement, lighting, mood, and format',
  'Aspect ratio controls for 2:3, 3:2, 1:1, 9:16, and 16:9 outputs',
  'Duration controls for 6-30-second clips',
  '480p and 720p Grok Imagine video resolution options',
  'Grok mode controls for Normal, Fun, and Spicy where supported',
  'Session preview, video history, and download workflow',
];

const EN_GROK_VIDEO_GENERATOR_DATA = {
  path: GROK_VIDEO_GENERATOR_PATH,
  seoTitle: GROK_VIDEO_GENERATOR_SEO_TITLE,
  seoDescription: GROK_VIDEO_GENERATOR_SEO_DESCRIPTION,
  datePublished: GROK_VIDEO_GENERATOR_DATE_PUBLISHED,
  lastUpdated: GROK_VIDEO_GENERATOR_LAST_UPDATED,
  lastUpdatedLabel: GROK_VIDEO_GENERATOR_LAST_UPDATED_LABEL,
  keywords: GROK_VIDEO_GENERATOR_KEYWORDS,
  quickAnswer: GROK_VIDEO_GENERATOR_QUICK_ANSWER,
  toolDetails: GROK_VIDEO_GENERATOR_TOOL_DETAILS,
  features: GROK_VIDEO_GENERATOR_FEATURES,
  steps: GROK_VIDEO_GENERATOR_STEPS,
  settings: GROK_VIDEO_GENERATOR_SUPPORTED_SETTINGS,
  examples: GROK_VIDEO_GENERATOR_EXAMPLES,
  mediaProof: GROK_VIDEO_GENERATOR_MEDIA_PROOF,
  comparisonRows: GROK_VIDEO_GENERATOR_COMPARISON_ROWS,
  useCases: GROK_VIDEO_GENERATOR_USE_CASES,
  valueRows: GROK_VIDEO_GENERATOR_VALUE_ROWS,
  limits: GROK_VIDEO_GENERATOR_LIMITS,
  faqs: GROK_VIDEO_GENERATOR_FAQS,
  schemaFeatures: GROK_VIDEO_GENERATOR_SCHEMA_FEATURES,
  copy: {
    direct: {
      eyebrow: 'Quick answer',
      title: 'Create a short AI video from one written scene.',
      cta: 'Try the generator',
    },
    quick: {
      eyebrow: 'Quick answer',
      title: 'What is a Grok video generator?',
      detailsTitle: 'Tool details',
    },
    media: {
      eyebrow: 'Video proof',
      title: 'Grok video generator examples with real demo media',
      description:
        'Each demo pairs a visible prompt with its settings and output clip. Compare motion, framing, timing, and product clarity before writing your own scene.',
      playLabel: 'Play',
      promptLabel: 'Prompt',
      settingLabel: 'Setting',
    },
    features: {
      eyebrow: 'Core controls',
      title: 'Core features for Grok video creation',
      description:
        'The Grok video generator keeps the prompt, model, format, duration, resolution, Grok mode, and preview controls close to the workspace so you can check the setup before spending credits.',
    },
    steps: {
      eyebrow: 'How to',
      title: 'How to use the Grok video generator',
      description:
        'A useful Grok video generator prompt does three things well: it names the scene, chooses the output format, and leaves room to review the result like a creative draft.',
    },
    settings: {
      eyebrow: 'Settings',
      title: 'Grok video creation settings',
      description:
        'Use these visible settings to decide whether the Grok video generator fits your prompt, channel, and review quality target.',
      headers: {
        setting: 'Setting',
        options: 'Available options',
        bestFor: 'Best for',
      },
    },
    promptExamples: {
      eyebrow: 'Prompt examples',
      title: 'Prompt examples for Grok video creation',
      description:
        'Each Grok video generator example includes the prompt, suggested setting, and expected output so you can adapt the structure without relying on a screenshot or hidden tab.',
      headers: {
        useCase: 'Use case',
        prompt: 'Prompt pattern',
        setting: 'Setting',
        result: 'Expected draft',
      },
    },
    comparison: {
      eyebrow: 'Choose a workflow',
      title: 'Grok video generator or Grok image to video?',
      description:
        'Choose the text workflow when the idea begins as words. Choose image to video when an existing frame already defines the subject, product, or visual style.',
      headers: {
        need: 'Decision point',
        thisTool: 'With this Grok video generator',
        generic: 'With Grok image to video',
      },
    },
    useCases: {
      eyebrow: 'Use cases',
      title: 'Where a Grok video creation tool fits best',
      description:
        'Use this workflow when you want to turn a written idea into a short motion draft before producing, editing, or publishing a final video.',
    },
    value: {
      eyebrow: 'Value',
      title: 'Why use this Grok video generator?',
      headers: {
        need: 'Need',
        helps: 'How it helps',
      },
    },
    limits: {
      eyebrow: 'Limits',
      title: 'Input requirements and review limits',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Grok video generator FAQ',
      description:
        'Check the supported input, duration, pricing factors, ownership, and publishing review requirements before generating a clip.',
      checksTitle: 'Common checks',
      checks: [
        'What it does',
        'Generator setup',
        'Prompt writing',
        'Tool ownership',
        'Publishing review',
      ],
    },
    cta: {
      eyebrow: 'Ready to direct the next clip',
      title: 'Write a scene in the Grok video generator.',
      description:
        'Start the Grok video generator with one clear scene, choose the output settings, then review the draft before publishing.',
      generate: 'Generate video',
      image: 'Animate an image with a start frame',
      history: 'View generated video history',
    },
    schema: {
      softwareName: 'Grok Video Generator',
      howToName: 'How to use the Grok video generator',
      howToDescription:
        'Write a scene prompt, choose video settings, then review and refine the generated video.',
      breadcrumbHome: 'Home',
      breadcrumbPage: 'Grok Video Generator',
    },
  },
};

type GrokVideoGeneratorSeoData = typeof EN_GROK_VIDEO_GENERATOR_DATA;

const ES_GROK_VIDEO_GENERATOR_DATA: GrokVideoGeneratorSeoData = {
  ...EN_GROK_VIDEO_GENERATOR_DATA,
  seoTitle: 'Grok video generator | Generador de video Grok',
  seoDescription:
    'Usa este Grok video generator para convertir una escena escrita en un video corto con prompt, ratio, duracion, resolucion y modo Grok.',
  lastUpdatedLabel: '28 de julio de 2026',
  keywords: [
    'grok video generator',
    'grok generador de video',
    'grok generador de video gratis',
    'grok ai video generator',
    'grok video gratis',
    'grok text to video',
    'grok video limit',
    'grok imagine video length limit 2026',
    'grok imagine video free limit',
    'grok imagine rate limit reached',
  ].join(', '),
  quickAnswer:
    'Un Grok video generator convierte una escena escrita en un borrador corto de video con IA. Este tool esta pensado para creadores, marketers y equipos que quieren controlar prompt, ratio, duracion, resolucion y modo Grok antes de gastar creditos.',
  toolDetails: [
    {
      label: 'Flujo principal',
      value: 'Grok text to video desde una escena escrita',
    },
    {
      label: 'Mejor entrada',
      value: 'Prompt claro con sujeto, accion, camara y ambiente',
    },
    {
      label: 'Ultima actualizacion',
      value: '28 de julio de 2026',
    },
  ],
  features: [
    {
      title: 'Convierte prompts de texto en videos cortos',
      body: 'Empieza con una escena escrita y transformala en un borrador de video. Un buen prompt de Grok text to video explica sujeto, accion, camara, escenario, ambiente y formato final.',
    },
    {
      title: 'Controla camara, movimiento y direccion de escena',
      body: 'Guia el generador con lenguaje de movimiento: push-in lento, paneo handheld, orbit shot, reveal de producto, barrido de luz, viento o close-up cinematografico.',
    },
    {
      title: 'Elige formato antes de gastar creditos',
      body: 'Selecciona aspecto, duracion, resolucion, modelo y modo Grok antes de generar. Asi el resultado encaja con social, anuncios, storyboards o previews de producto.',
    },
    {
      title: 'Revisa, guarda y reutiliza borradores',
      body: 'Previsualiza clips, compara intentos recientes, abre historial y descarga el mejor borrador cuando la direccion creativa esta lista.',
    },
  ],
  steps: [
    {
      name: 'Escribe la escena',
      text: 'Describe una idea clara: quien o que aparece, que se mueve, como se comporta la camara, que luz se siente y donde se usara el clip.',
    },
    {
      name: 'Elige ajustes de video',
      text: 'Selecciona modelo, relacion de aspecto, duracion, resolucion y modo Grok para Reels, Shorts, TikTok, X, anuncios o storyboards.',
    },
    {
      name: 'Revisa y refina',
      text: 'Comprueba consistencia del sujeto, calidad de movimiento, encuadre y uso final. Descarga o ajusta el prompt para otra version.',
    },
  ],
  supportedSettings: [
    {
      setting: 'Prompt',
      options: 'Escena escrita',
      bestFor:
        'Ideas de video con sujeto, accion, camara, ambiente y objetivo claros.',
    },
    {
      setting: 'Relacion de aspecto',
      options: '2:3, 3:2, 1:1, 9:16 o 16:9',
      bestFor:
        'Clips verticales, posts cuadrados, anuncios anchos, previews y storyboards.',
    },
    {
      setting: 'Duracion',
      options: '6, 10, 15, 20 o 30 segundos',
      bestFor:
        'Tests rapidos, hooks cortos, borradores sociales y previews de campana.',
    },
    {
      setting: 'Resolucion',
      options: '480p o 720p para Grok Imagine text-to-video',
      bestFor:
        'Borradores de bajo coste en 480p o revisiones mas nitidas en 720p.',
    },
    {
      setting: 'Modo Grok',
      options: 'Normal, Fun o Spicy donde este disponible',
      bestFor:
        'Ajustar el tono creativo manteniendo revision humana para trabajo publico o de marca.',
    },
  ],
  examples: [
    {
      title: 'Teaser de lanzamiento de producto',
      prompt:
        'Altavoz inalambrico negro mate sobre mesa de piedra, push-in lento, luz dorada lateral, particulas sutiles, mood premium.',
      setting: '9:16, 6 segundos, 720p',
      result: 'Borrador vertical para anuncios, producto o social launch.',
    },
    {
      title: 'Escena cinematografica de personaje',
      prompt:
        'Mensajero cyberpunk bajo lluvia neon, chaqueta moviendose con viento, camara orbitando lentamente, calle reflectante.',
      setting: '16:9, 10 segundos, 720p',
      result: 'Concept corto de movimiento para storyboard, pitch o mood test.',
    },
    {
      title: 'Clip de anuncio de fundador',
      prompt:
        'Oficina startup limpia con laptop abriendose para revelar una app, movimiento handheld suave, luz de manana, mood de lanzamiento.',
      setting: '1:1, 6 segundos, 720p',
      result:
        'Borrador cuadrado para LinkedIn, X, updates de producto o pitch.',
    },
  ],
  mediaProof: EN_GROK_VIDEO_GENERATOR_DATA.mediaProof.map((item) => ({
    ...item,
    label: 'Demo texto a video',
  })),
  comparisonRows: [
    {
      factor: 'Punto de partida',
      thisTool:
        'Empieza un borrador Grok text to video desde una escena escrita, aunque aun no tengas imagen fuente.',
      genericTool:
        'Muchos generadores mezclan texto, imagen, avatar y plantillas en una entrada saturada.',
    },
    {
      factor: 'Control creativo',
      thisTool:
        'Elige prompt, ratio, duracion, resolucion, modelo y modo Grok antes de generar.',
      genericTool:
        'Algunas herramientas esconden decisiones de formato hasta despues y obligan a probar mas borradores.',
    },
    {
      factor: 'Siguiente paso',
      thisTool:
        'Usa texto para una idea nueva o cambia a imagen a video si ya tienes un frame.',
      genericTool:
        'El usuario a menudo decide el flujo correcto despues de abrir un generador demasiado amplio.',
    },
    {
      factor: 'Revision',
      thisTool:
        'Previsualiza, compara, descarga o abre historial despues de cada intento.',
      genericTool:
        'Muchas landing pages se centran en la caja de prompt y dan poca guia de revision.',
    },
  ],
  useCases: [
    {
      title: 'Creadores sociales probando hooks cortos',
      body: 'Escribe una escena de hook, elige 9:16 y prueba movimiento antes de editar el post final para TikTok, Reels, Shorts o X.',
    },
    {
      title: 'Equipos de marketing explorando ideas de producto',
      body: 'Convierte copy de campana en reveal de producto, escena lifestyle o concepto de anuncio antes de producir assets finales.',
    },
    {
      title: 'Disenadores y founders haciendo storyboards',
      body: 'Visualiza lanzamientos, escenas de pitch, game scenes, moods cinematicos o conceptos de marca como clips cortos de revision.',
    },
  ],
  valueRows: [
    {
      need: 'Empezar sin imagen fuente',
      value:
        'Usa Grok text to video cuando la idea esta clara pero aun no tienes asset visual.',
    },
    {
      need: 'Reducir prompts vagos',
      value:
        'Estructura la idea por sujeto, accion, camara, luz, ambiente y formato.',
    },
    {
      need: 'Encajar con el canal',
      value: 'Elige vertical, cuadrado o ancho antes de generar.',
    },
    {
      need: 'Iterar con menos friccion',
      value:
        'Revisa el clip, ajusta el prompt y conserva resultados recientes en historial.',
    },
  ],
  limits: [
    'Usa prompts con un sujeto y un movimiento principal claros.',
    'Multitudes complejas, texto pequeno, manos detalladas y listas largas de acciones pueden bajar la consistencia.',
    'Revisa videos antes de anuncios, trabajo de cliente, producto o publicaciones publicas.',
    'Evita retratos protegidos, marcas, personajes con copyright o datos privados sin permiso.',
    'Este tool es un flujo independiente de Grok Imagine y no esta afiliado a xAI, Grok o X.',
  ],
  faqs: [
    {
      question: 'Que es un Grok video generator?',
      answer:
        'Es una herramienta que convierte un prompt escrito en un borrador corto de video con IA. Describes la escena, eliges ajustes y generas el clip.',
    },
    {
      question: 'Grok AI puede generar videos en 2026?',
      answer:
        'Si. En 2026 muchos usuarios preguntan si Grok puede generar videos porque el acceso puede depender del plan, modelo, rollout y limites actuales. Esta pagina te ayuda a empezar desde una escena escrita, elegir ajustes y generar un borrador corto de video estilo Grok.',
    },
    {
      question: 'Grok puede generar videos desde texto?',
      answer:
        'Si. Usa Grok video generator cuando la idea empieza como texto y no tienes una imagen fuente. Si ya tienes un primer frame fuerte, usa el flujo Grok image to video.',
    },
    {
      question: 'Puedo generar video con texto en Grok?',
      answer:
        'Si. Este flujo esta pensado para empezar desde una idea escrita. Si ya tienes una imagen fuente, usa imagen a video.',
    },
    {
      question: 'Grok video generation es gratis o de pago?',
      answer:
        'La disponibilidad y el coste dependen del modelo, ajustes de video, creditos de la cuenta y limites actuales del producto. Revisa el coste antes de generar y usa duraciones cortas para las primeras pruebas.',
    },
    {
      question: 'Existe un Grok Imagine video free limit?',
      answer:
        'El acceso gratis a video puede ser mas limitado que la exploracion de imagenes, porque cada borrador consume mas capacidad. Si aparece el limite gratis de video, prepara mejor el prompt, usa una duracion corta o anima una imagen guardada cuando tengas creditos.',
    },
    {
      question: 'Cual es el limite de duracion de Grok AI video generation?',
      answer:
        'Este flujo muestra duraciones cortas como 6, 10, 15, 20 y 30 segundos cuando estan disponibles. Usa clips cortos para probar prompts y clips mas largos solo cuando la escena, camara y movimiento esten claros.',
    },
    {
      question: 'Por que aparece un Grok video rate limit?',
      answer:
        'Un rate limit de video suele aparecer cuando se envian demasiadas solicitudes en poco tiempo, o cuando el modelo elegido esta bajo presion de cuenta, cola o creditos. Deja de repetir intentos, espera o baja el ritmo, y continua desde el mejor prompt o imagen guardada.',
    },
    {
      question: 'Que ajustes debo elegir?',
      answer:
        'Elige modelo, ratio, duracion, resolucion y modo Grok segun el canal. 9:16 va bien para social vertical y 720p para revisiones mas nitidas.',
    },
    {
      question: 'Como escribir un prompt de video para Grok?',
      answer:
        'Incluye sujeto, accion, movimiento de camara, fondo, luz, mood, duracion, relacion de aspecto y plataforma objetivo.',
    },
    {
      question: 'Grok video generator es gratis?',
      answer:
        'La disponibilidad y el coste dependen del modelo, ajustes, creditos y producto usado. Revisa el coste antes de generar.',
    },
    {
      question: 'Este sitio es oficial de Grok o xAI?',
      answer:
        'No. Es una herramienta independiente de flujo Grok Imagine y no esta afiliada a xAI, Grok o X.',
    },
    {
      question: 'Puedo usar videos generados comercialmente?',
      answer:
        'Depende del modelo, terminos de la plataforma, derechos de entrada y revision final. No uses personajes, marcas o retratos privados sin permiso.',
    },
  ],
  schemaFeatures: [
    'Generacion texto a video desde una escena escrita',
    'Controles de prompt para sujeto, accion, camara, luz, mood y formato',
    'Relaciones de aspecto 2:3, 3:2, 1:1, 9:16 y 16:9',
    'Duracion de 6, 10, 15, 20 y 30 segundos',
    'Resoluciones 480p y 720p para video Grok Imagine',
    'Modos Grok Normal, Fun o Spicy donde esten disponibles',
    'Preview, historial de video y descarga',
  ],
  copy: {
    ...EN_GROK_VIDEO_GENERATOR_DATA.copy,
    direct: {
      eyebrow: 'Respuesta directa',
      title:
        'Un Grok video generator convierte una escena escrita en un borrador corto con controles de prompt, ratio, duracion, resolucion y modo Grok.',
      cta: 'Usar generador',
    },
    quick: {
      eyebrow: 'Respuesta rapida',
      title: 'Que es un Grok video generator?',
      detailsTitle: 'Detalles del tool',
    },
    media: {
      eyebrow: 'Prueba de video',
      title: 'Ejemplos de Grok video generator con demos reales',
      description:
        'Cada ejemplo conecta prompt visible, ajustes, clip de salida y caption para entender un borrador revisable.',
      playLabel: 'Reproducir',
      promptLabel: 'Prompt',
      settingLabel: 'Ajuste',
    },
    features: {
      eyebrow: 'Funciones',
      title: 'Funciones clave para crear Grok text to video',
      description:
        'Prompt, modelo, formato, duracion, resolucion, modo Grok y preview permanecen cerca del workspace antes de gastar creditos.',
    },
    steps: {
      eyebrow: 'Como usarlo',
      title: 'Como usar el Grok video generator',
      description:
        'Un buen prompt nombra la escena, elige formato y deja margen para revisar el resultado como borrador creativo.',
    },
    settings: {
      eyebrow: 'Ajustes',
      title: 'Ajustes del generador de video Grok',
      description:
        'Revisa estos ajustes para saber si encajan con el prompt, canal y calidad de revision.',
      headers: {
        setting: 'Ajuste',
        options: 'Opciones',
        bestFor: 'Mejor para',
      },
    },
    promptExamples: {
      eyebrow: 'Prompts',
      title: 'Ejemplos de prompts Grok text to video',
      description:
        'Cada ejemplo incluye prompt, ajuste recomendado y resultado esperado.',
      headers: {
        useCase: 'Caso',
        prompt: 'Prompt',
        setting: 'Ajuste',
        result: 'Borrador esperado',
      },
    },
    comparison: {
      eyebrow: 'Por que elegirlo',
      title: 'Grok video generator vs herramientas amplias de video IA',
      description:
        'Este tool se concentra en Grok text to video controlable y deja claro cuando cambiar a imagen a video.',
      headers: {
        need: 'Necesidad',
        thisTool: 'En este generador Grok',
        generic: 'En una herramienta amplia',
      },
    },
    useCases: {
      eyebrow: 'Casos de uso',
      title: 'Cuando encaja Grok AI text to video',
      description:
        'Cuando quieres convertir una idea escrita en un borrador corto antes de producir, editar o publicar.',
    },
    value: {
      eyebrow: 'Valor',
      title: 'Por que usar este Grok video generator?',
      headers: {
        need: 'Necesidad',
        helps: 'Como ayuda',
      },
    },
    limits: {
      eyebrow: 'Limites',
      title: 'Buen input y fronteras',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'FAQ de Grok video generator',
      description:
        'Comprueba ajustes, prompt textual, propiedad del tool y revision segura antes de publicar.',
      checksTitle: 'Comprobar rapido',
      checks: [
        'Que hace el tool',
        'Ajustes texto a video',
        'Como escribir prompt',
        'Propiedad del tool',
        'Revision antes de publicar',
      ],
    },
    cta: {
      eyebrow: 'Listo para dirigir tu proximo clip?',
      title: 'Escribe una escena en el Grok video generator.',
      description:
        'Empieza con un prompt claro, elige ajustes y revisa el borrador Grok AI antes de publicar.',
      generate: 'Generar video',
      image: 'Animar una imagen inicial',
      history: 'Ver historial de videos',
    },
    schema: {
      softwareName: 'Grok video generator',
      howToName: 'Como usar el Grok video generator',
      howToDescription:
        'Escribe un prompt de escena, elige ajustes de video, genera, revisa y ajusta el resultado.',
      breadcrumbHome: 'Inicio',
      breadcrumbPage: 'Grok video generator',
    },
  },
};

export function getGrokVideoGeneratorSeoData(
  locale: Locale = getLocale()
): GrokVideoGeneratorSeoData {
  if (locale === 'es') return ES_GROK_VIDEO_GENERATOR_DATA;
  return EN_GROK_VIDEO_GENERATOR_DATA;
}
